import Link from 'next/link';
import pluralize from 'pluralize';
import { memo, useMemo } from 'react';

import { FileStatusIconCompleted, FileStatusSubtitle, FileStatusTitle } from '@air/feature-file-status/ui';

import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { Routes } from '~/constants/routes';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type BoardDeletionTask, type GetCompletedMetadata } from '~/store/tasks/types';

export type DeleteBoardCompletedPanelItemProps = {
  metadata: GetCompletedMetadata<BoardDeletionTask>;
  onClear: () => void;
};

export const DeleteBoardCompletedPanelItem = memo(
  ({ metadata: { boardIds }, onClear }: DeleteBoardCompletedPanelItemProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const recentlyDeletedLink = useMemo(
      () => (
        <Link
          href={Routes.media.recentlyDeleted}
          className="cursor-pointer font-semibold ads:text-accent text-altitude-9 ads:hover:text-accent-hover hover:text-altitude-11 hover:underline"
        >
          {ui2026 ? 'Recently Deleted' : 'Recently deleted'}
        </Link>
      ),
      [ui2026],
    );

    const isSingleBoard = boardIds.length === 1;
    const ui2026Title = isSingleBoard ? 'Board deleted' : `${pluralize('board', boardIds.length, true)} deleted`;
    const boardsMovedLabel = isSingleBoard ? 'Board' : pluralize('Board', boardIds.length, true);

    return (
      <PaneItemTW
        truncate={false}
        avatar={<FileStatusIconCompleted size={getTaskPaneFileStatusIconSize(ui2026)} />}
        title={<FileStatusTitle>{ui2026 ? ui2026Title : 'Deleted successfully'}</FileStatusTitle>}
        subtitle={
          <FileStatusSubtitle className="truncate">
            {ui2026 ? boardsMovedLabel : pluralize('Board', boardIds.length, true)} moved to {recentlyDeletedLink}
          </FileStatusSubtitle>
        }
        buttons={<TaskPaneDismissButton label="Clear" onClick={onClear} />}
      />
    );
  },
);

DeleteBoardCompletedPanelItem.displayName = 'DeleteBoardCompletedPanelItem';
